import Image from 'next/image';
import { useEffect, useState } from 'react';

type Certification = { src: string; alt: string };

const trainers = [
  {
    name: 'Marianna OLEFYRENKO',
    role: 'CEO & Co-Founder',
    tagline:
      'Agentic AI Expert, Agile Enthusiast, Google Developer Group Member',
    bio: '15+ years in IT across AI, Software Engineering, Data Analytics, and UX/UI. Marianna builds end‑to‑end, user‑centric products that turn complex AI into practical outcomes for businesses. She blends product strategy with hands‑on engineering to ship resilient, measurable solutions.',
    details: [
      'WSQ Advanced Certificate in Learning and Performance 2.0 (ACLP 2.0).',
      'ACLP Certified Trainer with 5+ years of experience training business owners.',
      'Professional Scrum Product Owner (PSPO), specializing in agile AI implementation.',
    ],
    avatar: '/images/about-us/marianna-linkedin.jpg',
    certifications: [
      { src: '/images/about-us/ACLP_Cert.png', alt: 'ACLP Certification' },
      { src: '/images/about-us/ISCA.png', alt: 'ISCA Member' },
      { src: '/images/about-us/NTU_AI.png', alt: 'NTU AI Certification' },
    ],
  },
  {
    name: 'Dmytro OLEFYRENKO',
    role: 'CTO & Founder',
    tagline:
      'Agentic AI Architect, Agile Practitioner, Google Developer Group Member',
    bio: '16+ years of experience in software engineering (Serverless, AWS, JavaScript/TypeScript, UX, AI). Dmytro is a seasoned technology leader with deep expertise in building scalable, cloud-native platforms. He has led cross-functional engineering teams of 15+ and delivered high-availability systems.',
    details: [
      'AWS Certified Developer, specializing in secure, scalable SaaS infrastructure.',
      'Google Mobile Web Specialist with a focus on web performance and security.',
      'GDG Member since 2018, contributing active research in LLMs and AI agent loops.',
    ],
    avatar: '/images/about-us/dmytro-linkedin.png',
    certifications: [
      {
        src: '/images/about-us/AWS_Dmytro_1.png',
        alt: 'AWS Developer Certified',
      },
      { src: '/images/about-us/AWS_Dmytro3.png', alt: 'AWS Certificate' },
      {
        src: '/images/about-us/Google_Dmytro_frame.png',
        alt: 'Google Mobile Web Specialist',
      },
    ],
  },
];

export default function TrainersSection() {
  const [activeCert, setActiveCert] = useState<Certification | null>(null);

  useEffect(() => {
    if (!activeCert) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveCert(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeCert]);

  return (
    <section className='relative overflow-hidden bg-slate-50 py-24 dark:bg-slate-950/40 border-t border-slate-100 dark:border-slate-900 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-3xl text-center'>
          <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
            Learn from the Creators
          </h2>
          <h3 className='mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'>
            Meet Your Workshops Trainers
          </h3>
          <p className='mt-4 text-lg leading-8 text-slate-600 dark:text-slate-400'>
            We don't just teach AI; we design and run a global AI platform. Get
            hands-on instruction from the founders of Easy-Peasy.AI.
          </p>
        </div>

        <div className='mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-12 lg:grid-cols-2'>
          {trainers.map((trainer) => (
            <div
              key={trainer.name}
              className='flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:scale-[1.01] hover:shadow-md dark:border-slate-800 dark:bg-slate-900/60'
            >
              <div>
                <div className='flex items-center gap-x-6'>
                  <div className='relative h-20 w-20 flex-shrink-0'>
                    <Image
                      className='rounded-full object-cover ring-2 ring-indigo-500/20'
                      src={trainer.avatar}
                      alt={trainer.name}
                      fill
                      sizes='80px'
                      priority
                    />
                  </div>
                  <div>
                    <h4 className='text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100'>
                      {trainer.name}
                    </h4>
                    <p className='text-sm font-semibold leading-6 text-indigo-600 dark:text-indigo-500'>
                      {trainer.role}
                    </p>
                  </div>
                </div>

                <p className='mt-6 text-sm font-medium leading-6 text-slate-600 dark:text-slate-300 italic'>
                  {trainer.tagline}
                </p>

                <p className='mt-4 text-base leading-7 text-slate-600 dark:text-slate-400'>
                  {trainer.bio}
                </p>

                <ul role='list' className='mt-6 space-y-3'>
                  {trainer.details.map((detail, idx) => (
                    <li
                      key={idx}
                      className='flex items-start gap-x-3 text-sm text-slate-600 dark:text-slate-400'
                    >
                      <svg
                        className='mt-1 h-4 w-4 flex-shrink-0 text-indigo-600 dark:text-indigo-500'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth='2'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Certifications Grid */}
              <div className='mt-10 border-t border-slate-200 pt-8 dark:border-slate-800'>
                <h5 className='text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                  Certifications & Compliances
                </h5>
                <div className='mt-4 grid grid-cols-3 gap-3'>
                  {trainer.certifications.map((cert, idx) => (
                    <button
                      key={idx}
                      type='button'
                      onClick={() => setActiveCert(cert)}
                      aria-label={`Enlarge ${cert.alt}`}
                      className='group relative flex h-32 items-center justify-center overflow-hidden rounded-lg sm:h-40'
                    >
                      <Image
                        className='max-h-full w-auto max-w-full object-contain transition-transform duration-200 group-hover:scale-105'
                        src={cert.src}
                        alt={cert.alt}
                        width={300}
                        height={180}
                      />
                      <span className='absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-md bg-slate-900/70 text-white opacity-80 transition group-hover:bg-indigo-600 group-hover:opacity-100'>
                        <svg
                          className='h-3 w-3'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6'
                          />
                        </svg>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {activeCert && (
        <div
          role='dialog'
          aria-modal='true'
          aria-label={activeCert.alt}
          onClick={() => setActiveCert(null)}
          className='fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 sm:p-8'
        >
          <button
            type='button'
            onClick={() => setActiveCert(null)}
            aria-label='Close'
            className='absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20'
          >
            <svg
              className='h-6 w-6'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className='flex max-h-full max-w-4xl flex-col items-center'
          >
            <Image
              src={activeCert.src}
              alt={activeCert.alt}
              width={1400}
              height={1000}
              className='h-auto max-h-[80vh] w-auto max-w-full rounded-xl bg-white object-contain shadow-2xl'
            />
            <p className='mt-4 text-center text-sm font-medium text-white'>
              {activeCert.alt}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
